import React from "react";

/**
 * Vector art for the marketing page, authored inline rather than sourced as
 * stock imagery: it stays crisp at any size, carries no network payload or
 * licence, and picks up the brand palette directly.
 */

const C = {
  ink: "#101828",
  line: "#e3e7ef",
  lineSoft: "#eef1f6",
  surface: "#ffffff",
  soft: "#f7f8fb",
  muted: "#aab1c0",
  green: "#00ac56",
  greenSoft: "#e8f7ef",
  blue: "#0e5fc1",
  blueSoft: "#eaf1fd",
  violet: "#4a3aa7",
  violetSoft: "#f1eefc",
  amber: "#eda100",
  amberSoft: "#fdf3e3",
};

/* ---------------------------------------------------------------- motion */
/**
 * Animation lives inside each <svg> as a scoped <style>. Authored SVG rather
 * than a GIF: it stays sharp at any size, weighs a fraction as much, keeps the
 * brand palette, and can honour prefers-reduced-motion — which a GIF cannot.
 */
const MOTION_CSS = `
  @keyframes lpaRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  @keyframes lpaDraw { to { stroke-dashoffset: 0; } }
  @keyframes lpaPulse { 0%, 100% { opacity: .35; transform: scale(1); } 50% { opacity: 1; transform: scale(1.35); } }
  @keyframes lpaBar { from { transform: scaleY(.35); } to { transform: scaleY(1); } }
  @keyframes lpaSheen { 0% { transform: translateX(-120%); } 55%, 100% { transform: translateX(320%); } }
  @keyframes lpaTick { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
  @keyframes lpaGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  @keyframes lpaBlink { 0%,100% { opacity: .25; } 50% { opacity: 1; } }

  .lpa-rise  { animation: lpaRise .7s cubic-bezier(.2,.8,.2,1) both; }
  .lpa-pulse { animation: lpaPulse 2.6s ease-in-out infinite; transform-origin: center; }
  .lpa-draw  { stroke-dasharray: 620; stroke-dashoffset: 620; animation: lpaDraw 2.4s cubic-bezier(.4,0,.2,1) .2s forwards; }
  .lpa-bar   { transform-origin: bottom; animation: lpaBar .8s cubic-bezier(.2,.8,.2,1) both; }
  .lpa-sheen { animation: lpaSheen 5.5s cubic-bezier(.4,0,.2,1) infinite; }
  .lpa-tick  { animation: lpaTick 3.2s ease-in-out infinite; }
  .lpa-grow  { transform-origin: left; animation: lpaGrow 1.6s cubic-bezier(.2,.8,.2,1) both; }
  .lpa-blink { animation: lpaBlink 1.4s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .lpa-rise, .lpa-pulse, .lpa-draw, .lpa-bar,
    .lpa-sheen, .lpa-tick, .lpa-grow, .lpa-blink { animation: none !important; }
    .lpa-draw { stroke-dashoffset: 0; }
  }
`;

/* ------------------------------------------------------------------ hero */
/**
 * Motion scoped to the hero board, on one 9s loop: a cursor picks a card up in
 * "In progress", a drop target opens in "Done", the card lands, both column
 * counts change, the sprint bar steps forward and a toast confirms the move.
 *
 * Every animated element is a child of the group that positions it. CSS
 * `transform` on an element replaces its SVG `transform` attribute, so the two
 * never sit on the same node — otherwise the cards collapse into the corner.
 */
const HERO_MOTION_CSS = `
  @keyframes lphDrag {
    0%, 6%   { transform: translate(0,0) rotate(0deg) scale(1); }
    12%      { transform: translate(4px,-10px) rotate(-2.5deg) scale(1.05); }
    44%      { transform: translate(396px,-122px) rotate(2deg) scale(1.05); }
    52%      { transform: translate(396px,-114px) rotate(0deg) scale(1); }
    90%      { transform: translate(396px,-114px) rotate(0deg) scale(1); opacity: 1; }
    95%      { transform: translate(396px,-114px) scale(1); opacity: 0; }
    95.5%    { transform: translate(0,0) scale(1); opacity: 0; }
    100%     { transform: translate(0,0) scale(1); opacity: 1; }
  }
  @keyframes lphShadow { 0%, 7% { opacity: 0; } 14%, 44% { opacity: 1; } 52%, 100% { opacity: 0; } }
  @keyframes lphCursor {
    0%       { opacity: 0; transform: translate(40px,40px); }
    5%       { opacity: 1; transform: translate(0,0); }
    9%       { transform: translate(0,0) scale(.86); }
    12%, 44% { opacity: 1; transform: translate(0,0) scale(.86); }
    50%      { opacity: 1; transform: translate(0,0) scale(1); }
    58%, 100%{ opacity: 0; transform: translate(26px,30px); }
  }
  @keyframes lphTarget { 0%, 10% { opacity: 0; } 18%, 44% { opacity: 1; } 51%, 100% { opacity: 0; } }
  @keyframes lphBefore { 0%, 50% { opacity: 1; } 51%, 94% { opacity: 0; } 96%, 100% { opacity: 1; } }
  @keyframes lphAfter  { 0%, 50% { opacity: 0; } 51%, 94% { opacity: 1; } 96%, 100% { opacity: 0; } }
  @keyframes lphStep   { 0%, 50% { transform: scaleX(.64); } 58%, 92% { transform: scaleX(.76); } 100% { transform: scaleX(.64); } }
  @keyframes lphToast  { 0%, 52% { opacity: 0; transform: translateY(14px); } 58%, 86% { opacity: 1; transform: none; } 92%, 100% { opacity: 0; transform: translateY(8px); } }
  @keyframes lphRipple { 0%, 50% { transform: scale(.4); opacity: .6; } 74%, 100% { transform: scale(1.9); opacity: 0; } }
  @keyframes lphSpark  { 0%, 50% { opacity: 0; transform: scale(.3); } 60% { opacity: 1; transform: scale(1); } 78%, 100% { opacity: 0; transform: scale(.5); } }
  @keyframes lphTrace  { to { stroke-dashoffset: 0; } }

  .lph-drag, .lph-cursor { transform-box: fill-box; transform-origin: center; }
  .lph-drag   { animation: lphDrag 9s cubic-bezier(.55,0,.2,1) infinite; }
  .lph-shadow { animation: lphShadow 9s linear infinite; }
  .lph-cursor { animation: lphCursor 9s cubic-bezier(.5,0,.2,1) infinite; }
  .lph-target { animation: lphTarget 9s ease-in-out infinite; }
  .lph-before { animation: lphBefore 9s steps(1, end) infinite; }
  .lph-after  { animation: lphAfter 9s steps(1, end) infinite; }
  .lph-step   { animation: lphStep 9s cubic-bezier(.2,.8,.2,1) infinite; transform-box: fill-box; transform-origin: left center; }
  .lph-toast  { animation: lphToast 9s cubic-bezier(.2,.8,.2,1) infinite; }
  .lph-ripple { animation: lphRipple 9s cubic-bezier(.2,.8,.2,1) infinite; transform-box: fill-box; transform-origin: center; }
  .lph-spark  { animation: lphSpark 9s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
  .lph-trace  { stroke-dasharray: 300; stroke-dashoffset: 300; animation: lphTrace 2.6s cubic-bezier(.4,0,.2,1) .4s forwards; }

  @media (prefers-reduced-motion: reduce) {
    .lph-drag, .lph-shadow, .lph-cursor, .lph-target, .lph-before, .lph-after,
    .lph-step, .lph-toast, .lph-ripple, .lph-spark, .lph-trace { animation: none !important; }
    .lph-trace { stroke-dashoffset: 0; }
    .lph-after, .lph-toast, .lph-cursor, .lph-shadow, .lph-target, .lph-ripple, .lph-spark { opacity: 0; }
  }
`;

const FONT = "Inter, 'SF Pro Text', system-ui, -apple-system, sans-serif";
const TYPE_TONES = [C.green, C.blue, "#e5493a", C.violet];

/* One issue card. Everything is abstract on purpose — it reads as the product
   without pretending to be a screenshot of real data. */
const HeroCard = ({ tone = 0, w1 = 128, w2 = 86, pts = 3, av = 1, prio = 1 }) => (
  <>
    <rect width="182" height="100" rx="14" fill={C.surface} stroke={C.line} filter="url(#lphCard)" />
    {/* issue type as a stripe down the edge */}
    <rect x="9" y="14" width="3.5" height="72" rx="1.75" fill={TYPE_TONES[tone % 4]} />
    <rect x="22" y="15" width="12" height="12" rx="3.5" fill={TYPE_TONES[tone % 4]} opacity="0.9" />
    <rect x="40" y="18" width="38" height="6.5" rx="3.25" fill={C.muted} opacity="0.45" />
    {/* priority chevrons */}
    {Array.from({ length: prio }, (_, i) => (
      <path key={i} d={`M152 ${23 - i * 4.5} l5 -4 l5 4`} fill="none" stroke={prio > 1 ? "#e5493a" : C.amber}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    ))}
    <rect x="22" y="38" width={w1} height="8.5" rx="4.25" fill="#d5dbe6" />
    <rect x="22" y="53" width={w2} height="8.5" rx="4.25" fill={C.lineSoft} />
    <rect x="22" y="72" width="34" height="15" rx="7.5" fill={tone % 2 ? C.blueSoft : C.violetSoft} />
    <rect x="30" y="77.5" width="18" height="4" rx="2" fill={tone % 2 ? C.blue : C.violet} opacity="0.55" />
    <circle cx="124" cy="79.5" r="9" fill={C.soft} stroke={C.line} />
    <text x="124" y="83.3" textAnchor="middle" fontFamily={FONT} fontSize="10" fontWeight="700" fill="#5b6475">{pts}</text>
    <circle cx="156" cy="79.5" r="11.5" fill={`url(#lphAv${av})`} stroke={C.surface} strokeWidth="2.5" />
  </>
);

/* Column headers and their cards. "In progress" is a card short because its
   lower card is the one in flight; "Done" keeps its top slot free to land in. */
const HERO_COLUMNS = [
  { x: 160, accent: C.muted, label: 56, count: "3", cards: [
    { tone: 0, w1: 132, w2: 86, pts: 3, av: 1, prio: 1 },
    { tone: 1, w1: 112, w2: 70, pts: 5, av: 3, prio: 2 },
    { tone: 3, w1: 124, w2: 92, pts: 2, av: 4, prio: 1 },
  ] },
  { x: 358, accent: C.blue, label: 78, before: "2", after: "1", cards: [{ tone: 1, w1: 118, w2: 80, pts: 8, av: 2, prio: 2 }] },
  { x: 556, accent: C.violet, label: 60, count: "2", cards: [
    { tone: 2, w1: 126, w2: 74, pts: 3, av: 3, prio: 2 },
    { tone: 0, w1: 108, w2: 88, pts: 1, av: 1, prio: 1 },
  ] },
  { x: 754, accent: C.green, label: 44, before: "2", after: "3", cards: [null, { tone: 1, w1: 120, w2: 78, pts: 5, av: 4, prio: 1 }] },
];

const CountPill = ({ x, children, className }) => (
  <g className={className}>
    <rect x={x} y="148" width="26" height="18" rx="9" fill={C.soft} stroke={C.lineSoft} />
    <text x={x + 13} y="161" textAnchor="middle" fontFamily={FONT} fontSize="11" fontWeight="700" fill="#5b6475">{children}</text>
  </g>
);

/** Stylised sprint board — the product's centrepiece screen. */
export const HeroBoardArt = () => (
  <svg viewBox="0 0 960 540" role="img" aria-label="Projam sprint board: a card is dragged from In progress to Done">
    <style>{MOTION_CSS + HERO_MOTION_CSS}</style>
    <defs>
      <linearGradient id="lphBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fcfdff" />
        <stop offset="100%" stopColor="#eef2f8" />
      </linearGradient>
      <radialGradient id="lphBlobA">
        <stop offset="0%" stopColor={C.green} stopOpacity="0.2" />
        <stop offset="100%" stopColor={C.green} stopOpacity="0" />
      </radialGradient>
      <radialGradient id="lphBlobB">
        <stop offset="0%" stopColor={C.blue} stopOpacity="0.16" />
        <stop offset="100%" stopColor={C.blue} stopOpacity="0" />
      </radialGradient>
      <linearGradient id="lphAv1" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3ad79c" /><stop offset="100%" stopColor="#059048" /></linearGradient>
      <linearGradient id="lphAv2" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#5ea6ff" /><stop offset="100%" stopColor="#0e5fc1" /></linearGradient>
      <linearGradient id="lphAv3" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#9d8cf2" /><stop offset="100%" stopColor="#4a3aa7" /></linearGradient>
      <linearGradient id="lphAv4" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#f7cb70" /><stop offset="100%" stopColor="#d08700" /></linearGradient>
      <linearGradient id="lphProgress" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#22d17f" /><stop offset="100%" stopColor="#00ac56" /></linearGradient>
      <linearGradient id="lphSheen" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="50%" stopColor="#ffffff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      <filter id="lphCard" x="-25%" y="-25%" width="150%" height="165%">
        <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="#0f172a" floodOpacity="0.09" />
      </filter>
      <filter id="lphRaised" x="-40%" y="-40%" width="180%" height="200%">
        <feDropShadow dx="0" dy="18" stdDeviation="16" floodColor="#0f172a" floodOpacity="0.28" />
      </filter>
      <clipPath id="lphFrame"><rect width="960" height="540" rx="2" /></clipPath>
    </defs>

    <g clipPath="url(#lphFrame)">
      <rect width="960" height="540" fill="url(#lphBg)" />
      <circle cx="820" cy="140" r="270" fill="url(#lphBlobA)" />
      <circle cx="260" cy="480" r="250" fill="url(#lphBlobB)" />

      {/* ------------------------------------------------------- app chrome */}
      <rect x="0" y="0" width="960" height="52" fill={C.surface} />
      <line x1="0" y1="52" x2="960" y2="52" stroke={C.line} />
      <circle cx="26" cy="26" r="5.5" fill="#ff5f57" />
      <circle cx="46" cy="26" r="5.5" fill="#febc2e" />
      <circle cx="66" cy="26" r="5.5" fill="#28c840" />

      <rect x="100" y="15" width="220" height="22" rx="11" fill={C.soft} />
      <circle cx="117" cy="25" r="4.5" fill="none" stroke={C.muted} strokeWidth="1.8" />
      <line x1="120.5" y1="28.5" x2="124" y2="32" stroke={C.muted} strokeWidth="1.8" strokeLinecap="round" />
      <rect x="134" y="22" width="92" height="7" rx="3.5" fill={C.line} />

      <rect x="610" y="15" width="128" height="22" rx="11" fill={C.greenSoft} />
      <circle cx="626" cy="26" r="4" fill={C.green} className="lpa-pulse" />
      <text x="638" y="30" fontFamily={FONT} fontSize="11" fontWeight="700" fill="#0b7a45">Sprint on track</text>

      <rect x="752" y="14" width="88" height="24" rx="12" fill="url(#lphProgress)" />
      <rect x="764" y="25" width="11" height="2.6" rx="1.3" fill={C.surface} />
      <rect x="768.2" y="20.8" width="2.6" height="11" rx="1.3" fill={C.surface} />
      <text x="782" y="30" fontFamily={FONT} fontSize="11" fontWeight="700" fill={C.surface}>Create</text>

      <rect x="854" y="14" width="24" height="24" rx="8" fill={C.soft} />
      <path d="M866 20 a5 5 0 0 1 5 5 v4 l2 2 h-14 l2 -2 v-4 a5 5 0 0 1 5 -5 z" fill={C.muted} opacity="0.7" />
      <circle cx="874" cy="18" r="3.5" fill="#ef4444" className="lpa-blink" />
      <circle cx="908" cy="26" r="13" fill="url(#lphAv2)" stroke={C.surface} strokeWidth="2.5" />

      {/* ---------------------------------------------------------- sidebar */}
      <rect x="0" y="52" width="136" height="488" fill={C.surface} />
      <line x1="136" y1="52" x2="136" y2="540" stroke={C.line} />
      <rect x="16" y="72" width="30" height="30" rx="9" fill="url(#lphProgress)" />
      <text x="31" y="92" textAnchor="middle" fontFamily={FONT} fontSize="14" fontWeight="800" fill={C.surface}>P</text>
      <rect x="54" y="79" width="58" height="9" rx="4.5" fill="#d5dbe6" />
      <rect x="54" y="93" width="36" height="6" rx="3" fill={C.lineSoft} />

      <rect x="10" y="124" width="116" height="32" rx="10" fill={C.greenSoft} />
      <rect x="22" y="134" width="12" height="12" rx="3" fill="none" stroke={C.green} strokeWidth="2" />
      <rect x="42" y="135" width="62" height="10" rx="5" fill={C.green} opacity="0.5" />
      {[170, 206, 242, 278].map((y, i) => (
        <g key={y} transform={`translate(0, ${y})`}>
          <g className="lpa-rise" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
            <rect x="22" y="2" width="12" height="12" rx="3" fill="none" stroke={C.muted} strokeWidth="2" opacity="0.6" />
            <rect x="42" y="3" width={68 - i * 9} height="10" rx="5" fill={C.line} />
          </g>
        </g>
      ))}

      <rect x="12" y="430" width="112" height="84" rx="14" fill={C.blueSoft} />
      <text x="26" y="452" fontFamily={FONT} fontSize="10.5" fontWeight="700" fill={C.blue}>Sprint 14</text>
      <rect x="26" y="462" width="84" height="6" rx="3" fill={C.surface} />
      <rect x="26" y="462" width="84" height="6" rx="3" fill={C.blue} opacity="0.7" className="lph-step" />
      <text x="26" y="490" fontFamily={FONT} fontSize="10" fill="#4b6b9b">6 days left</text>

      {/* --------------------------------------------------- sprint summary */}
      <rect x="160" y="68" width="776" height="52" rx="16" fill={C.surface} stroke={C.line} filter="url(#lphCard)" />
      <text x="180" y="99" fontFamily={FONT} fontSize="14" fontWeight="800" fill={C.ink}>Sprint board</text>
      <rect x="290" y="90" width="270" height="8" rx="4" fill={C.lineSoft} />
      <rect x="290" y="90" width="270" height="8" rx="4" fill="url(#lphProgress)" className="lph-step" />
      <g className="lph-before"><text x="574" y="98" fontFamily={FONT} fontSize="11.5" fontWeight="700" fill="#0b7a45">64%</text></g>
      <g className="lph-after"><text x="574" y="98" fontFamily={FONT} fontSize="11.5" fontWeight="700" fill="#0b7a45">76%</text></g>

      {/* the sprint's shape at a glance, drawn in as the board settles */}
      <polyline points="640,104 662,99 684,101 706,93 728,88 750,82" fill="none"
        stroke={C.blue} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="lph-trace" />
      <circle cx="750" cy="82" r="4" fill={C.blue} stroke={C.surface} strokeWidth="2" />

      {[{ cx: 822, g: 1 }, { cx: 844, g: 3 }, { cx: 866, g: 4 }].map((a) => (
        <circle key={a.cx} cx={a.cx} cy="94" r="13" fill={`url(#lphAv${a.g})`} stroke={C.surface} strokeWidth="2.5" />
      ))}
      <circle cx="888" cy="94" r="13" fill={C.soft} stroke={C.line} strokeWidth="1.5" />
      <text x="888" y="98" textAnchor="middle" fontFamily={FONT} fontSize="10" fontWeight="700" fill="#5b6475">+4</text>

      {/* ---------------------------------------------------------- columns */}
      {HERO_COLUMNS.map((col, ci) => (
        <g key={col.x}>
          <rect x={col.x} y="136" width="182" height="42" rx="14" fill={C.surface} stroke={C.line} filter="url(#lphCard)" />
          <rect x={col.x + 14} y="136" width="40" height="3" rx="1.5" fill={col.accent} />
          <circle cx={col.x + 22} cy="157" r="4" fill={col.accent} />
          <rect x={col.x + 34} y="152" width={col.label} height="10" rx="5" fill="#d5dbe6" />
          {col.count ? <CountPill x={col.x + 144}>{col.count}</CountPill> : null}
          {col.before ? <CountPill x={col.x + 144} className="lph-before">{col.before}</CountPill> : null}
          {col.after ? <CountPill x={col.x + 144} className="lph-after">{col.after}</CountPill> : null}

          {col.cards.map((card, i) => card && (
            <g key={i} transform={`translate(${col.x}, ${192 + i * 114})`}>
              <g className="lpa-rise" style={{ animationDelay: `${0.2 + ci * 0.1 + i * 0.08}s` }}>
                <HeroCard {...card} />
              </g>
            </g>
          ))}
        </g>
      ))}

      {/* where the card will land, opening while it travels */}
      <g className="lph-target">
        <rect x="754" y="192" width="182" height="100" rx="14" fill={C.greenSoft} stroke={C.green}
          strokeWidth="1.8" strokeDasharray="7 6" />
        <text x="845" y="246" textAnchor="middle" fontFamily={FONT} fontSize="11.5" fontWeight="700" fill="#0b7a45">Drop here</text>
      </g>

      {/* the card in flight: In progress, lower slot -> Done, top slot */}
      <g transform="translate(358, 306)">
        <g className="lph-drag">
          <rect x="8" y="10" width="166" height="88" rx="14" fill="#0f172a" opacity="0.001" />
          <g className="lph-shadow" opacity="0">
            <rect x="6" y="6" width="170" height="92" rx="14" fill={C.surface} filter="url(#lphRaised)" />
          </g>
          <HeroCard tone={2} w1={124} w2={92} pts={5} av={2} prio={2} />
          {/* the cursor holding it */}
          <g className="lph-cursor" opacity="0">
            <g transform="translate(150, 44)">
              <path d="M0 0 L0 22 L6 16.5 L10.5 26 L14.5 24.2 L10 15 L18 15 Z" fill={C.ink} stroke={C.surface}
                strokeWidth="2" strokeLinejoin="round" />
            </g>
          </g>
        </g>
      </g>

      {/* it lands */}
      <circle cx="845" cy="242" r="40" fill="none" stroke={C.green} strokeWidth="2.5" className="lph-ripple" opacity="0" />
      {[{ x: 768, y: 186, d: 0 }, { x: 930, y: 204, d: 0.12 }, { x: 790, y: 300, d: 0.24 }].map((p) => (
        <g key={p.x} transform={`translate(${p.x}, ${p.y})`}>
          <path className="lph-spark" style={{ animationDelay: `${p.d}s` }} opacity="0"
            d="M0 -7 Q1.6 -1.6 7 0 Q1.6 1.6 0 7 Q-1.6 1.6 -7 0 Q-1.6 -1.6 0 -7 Z" fill={C.green} />
        </g>
      ))}

      {/* and the board says so */}
      <g transform="translate(700, 470)">
        <g className="lph-toast" opacity="0">
          <rect width="220" height="44" rx="14" fill={C.ink} filter="url(#lphRaised)" />
          <circle cx="24" cy="22" r="11" fill={C.green} />
          <path d="M18.5 22.5 L22.5 26.5 L29.5 18.5" fill="none" stroke={C.surface} strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" />
          <text x="44" y="20" fontFamily={FONT} fontSize="12" fontWeight="700" fill={C.surface}>Moved to Done</text>
          <text x="44" y="34" fontFamily={FONT} fontSize="10.5" fill="#aab1c0">Sprint is 76% complete</text>
        </g>
      </g>

      {/* light sweep across the whole board */}
      <g className="lpa-sheen">
        <rect x="-160" y="0" width="150" height="540" fill="url(#lphSheen)" opacity="0.45" />
      </g>
    </g>
  </svg>
);

/* -------------------------------------------------------------- showcase */
/** Burn-down: the ideal guide against actual remaining work. */
export const BurndownArt = () => (
  <svg viewBox="0 0 560 360" role="img" aria-label="Sprint burn-down chart">
    <style>{MOTION_CSS}</style>
    <defs>
      <linearGradient id="lpArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={C.blue} stopOpacity="0.22" />
        <stop offset="100%" stopColor={C.blue} stopOpacity="0" />
      </linearGradient>
    </defs>

    <rect width="560" height="360" rx="16" fill={C.surface} />

    <rect x="26" y="24" width="132" height="11" rx="5.5" fill={C.line} />
    <rect x="26" y="44" width="84" height="8" rx="4" fill={C.lineSoft} />
    <rect x="446" y="24" width="88" height="26" rx="13" fill={C.greenSoft} />
    <circle cx="462" cy="37" r="4" fill={C.green} className="lpa-pulse" />
    <rect x="474" y="33" width="48" height="8" rx="4" fill={C.green} opacity="0.45" />

    {[100, 148, 196, 244].map((y) => (
      <line key={y} x1="64" y1={y} x2="526" y2={y} stroke={C.lineSoft} strokeDasharray="4 5" />
    ))}
    <line x1="64" y1="292" x2="526" y2="292" stroke={C.line} />

    {[
      [96, "40"], [144, "30"], [192, "20"], [240, "10"], [288, "0"],
    ].map(([y, t]) => (
      <text key={y} x="52" y={y + 4} textAnchor="end" fontSize="10" fill={C.muted} fontFamily="inherit">{t}</text>
    ))}

    <rect x="196" y="84" width="50" height="208" fill={C.soft} />
    <rect x="392" y="84" width="50" height="208" fill={C.soft} />

    <path
      d="M70,96 L132,120 L196,146 L246,146 L308,178 L370,204 L442,204 L520,242 L520,292 L70,292 Z"
      fill="url(#lpArea)"
    />

    <polyline points="70,96 520,278" fill="none" stroke="#94a3b8" strokeWidth="2"
      strokeDasharray="6 6" strokeLinecap="round" />

    <polyline
      points="70,96 132,120 196,146 246,146 308,178 370,204 442,204 520,242"
      fill="none" stroke={C.blue} strokeWidth="3"
      strokeLinecap="round" strokeLinejoin="round" className="lpa-draw"
    />
    {[[70, 96], [132, 120], [196, 146], [246, 146], [308, 178], [370, 204], [442, 204], [520, 242]].map(
      ([cx, cy], i) => (
        <circle key={cx} cx={cx} cy={cy} r="4.5" fill={C.blue} stroke={C.surface} strokeWidth="2.5"
          className="lpa-rise" style={{ animationDelay: `${0.35 + i * 0.2}s` }} />
      )
    )}

    <g className="lpa-tick">
      <rect x="452" y="150" width="86" height="34" rx="11" fill={C.surface} stroke={C.line} />
      <circle cx="468" cy="167" r="4" fill={C.blue} />
      <rect x="480" y="158" width="44" height="7" rx="3.5" fill={C.line} />
      <rect x="480" y="170" width="28" height="6" rx="3" fill={C.lineSoft} />
    </g>

    <g>
      <circle cx="200" cy="326" r="4.5" fill={C.blue} />
      <rect x="212" y="322" width="58" height="8" rx="4" fill={C.line} />
      <rect x="292" y="325" width="20" height="2.5" rx="1.25" fill="#94a3b8" />
      <rect x="320" y="322" width="38" height="8" rx="4" fill={C.line} />
    </g>
  </svg>
);

/** Collaboration: comments and mentions on an issue. */
export const CollaborationArt = () => (
  <svg viewBox="0 0 560 360" role="img" aria-label="Team collaborating on an issue">
    <style>{MOTION_CSS}</style>
    <defs>
      <filter id="lpRowShadow" x="-10%" y="-30%" width="120%" height="170%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0f172a" floodOpacity="0.07" />
      </filter>
    </defs>

    <rect width="560" height="360" rx="16" fill={C.surface} />

    <rect x="26" y="26" width="34" height="16" rx="8" fill={C.blueSoft} />
    <rect x="34" y="31" width="18" height="6" rx="3" fill={C.blue} opacity="0.5" />
    <rect x="70" y="27" width="184" height="13" rx="6.5" fill={C.line} />
    <rect x="26" y="54" width="124" height="9" rx="4.5" fill={C.lineSoft} />

    {[
      { y: 88, tint: C.greenSoft, dot: C.green, w: 330 },
      { y: 172, tint: C.blueSoft, dot: C.blue, w: 270 },
    ].map((row, i) => (
      <g key={row.y} className="lpa-rise" style={{ animationDelay: `${0.15 + i * 0.32}s` }}>
        <rect x="26" y={row.y} width="508" height="68" rx="16" fill={C.surface}
          stroke={C.line} filter="url(#lpRowShadow)" />
        <circle cx="62" cy={row.y + 34} r="18" fill={row.tint} />
        <circle cx="62" cy={row.y + 34} r="6" fill={row.dot} />
        <rect x="94" y={row.y + 16} width="104" height="10" rx="5" fill={C.line} />
        <rect x="208" y={row.y + 17} width="52" height="8" rx="4" fill={C.lineSoft} />
        <rect x="94" y={row.y + 36} width={row.w} height="9" rx="4.5" fill={C.lineSoft} />
        {i === 1 && <rect x="94" y={row.y + 36} width="70" height="9" rx="4.5" fill={C.blue} opacity="0.26" />}
        <rect x="440" y={row.y + 44} width="34" height="14" rx="7" fill={C.soft} />
        <rect x="482" y={row.y + 44} width="34" height="14" rx="7" fill={C.soft} />
      </g>
    ))}

    {/* someone is typing */}
    <g className="lpa-rise" style={{ animationDelay: "0.8s" }}>
      <rect x="26" y="256" width="508" height="62" rx="16" fill={C.soft} stroke={C.lineSoft} />
      <circle cx="62" cy="287" r="18" fill={C.violetSoft} />
      <circle cx="62" cy="287" r="6" fill={C.violet} />
      <rect x="94" y="272" width="96" height="9" rx="4.5" fill={C.line} />
      <g>
        <circle cx="100" cy="298" r="4" fill={C.muted} className="lpa-blink" />
        <circle cx="114" cy="298" r="4" fill={C.muted} className="lpa-blink" style={{ animationDelay: "0.2s" }} />
        <circle cx="128" cy="298" r="4" fill={C.muted} className="lpa-blink" style={{ animationDelay: "0.4s" }} />
      </g>
      <rect x="446" y="274" width="70" height="26" rx="13" fill={C.green} opacity="0.9" />
      <rect x="464" y="284" width="34" height="7" rx="3.5" fill="#ffffff" opacity="0.9" />
    </g>
  </svg>
);

/** Backlog planned into a sprint. */
export const PlanningArt = () => (
  <svg viewBox="0 0 560 360" role="img" aria-label="Backlog planned into a sprint">
    <style>{MOTION_CSS}</style>
    <rect width="560" height="360" rx="16" fill={C.surface} />

    <rect x="26" y="24" width="250" height="40" rx="14" fill={C.greenSoft} stroke="#cdeddd" />
    <circle cx="50" cy="44" r="6" fill={C.green} className="lpa-pulse" />
    <rect x="66" y="35" width="110" height="9" rx="4.5" fill={C.green} opacity="0.5" />
    <rect x="66" y="49" width="66" height="7" rx="3.5" fill={C.green} opacity="0.28" />
    <rect x="418" y="30" width="116" height="28" rx="14" fill={C.blueSoft} />
    <rect x="436" y="40" width="80" height="8" rx="4" fill={C.blue} opacity="0.45" />

    {[86, 142, 198].map((y, i) => (
      <g key={y} className="lpa-rise" style={{ animationDelay: `${0.2 + i * 0.26}s` }}>
        <rect x="26" y={y} width="508" height="46" rx="14" fill={C.surface} stroke={C.line} />
        <g className="lpa-tick" style={{ animationDelay: `${i * 0.5}s` }}>
          <circle cx="46" cy={y + 23} r="2" fill={C.muted} />
          <circle cx="46" cy={y + 17} r="2" fill={C.muted} />
          <circle cx="46" cy={y + 29} r="2" fill={C.muted} />
        </g>
        <rect x="62" y={y + 17} width="12" height="12" rx="4" fill={[C.blue, C.violet, C.amber][i]} />
        <rect x="84" y={y + 18} width={228 - i * 30} height="10" rx="5" fill={C.line} />
        <rect x="360" y={y + 16} width="30" height="14" rx="7" fill={C.blueSoft} />
        <rect x="400" y={y + 16} width="42" height="14" rx="7" fill={C.soft} />
        <circle cx="482" cy={y + 23} r="12" fill={[C.greenSoft, C.violetSoft, C.blueSoft][i]} />
      </g>
    ))}

    <rect x="26" y="264" width="508" height="70" rx="16" fill={C.soft}
      strokeDasharray="7 7" stroke={C.line} />
    <rect x="212" y="286" width="136" height="11" rx="5.5" fill={C.line} />
    <rect x="240" y="306" width="80" height="8" rx="4" fill={C.lineSoft} />
  </svg>
);

/* --------------------------------------------------------------- glyphs */
const glyph = (paths) => () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);

export const IconBoard = glyph(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M9 4v16M15 4v16" />
  </>
);

export const IconBacklog = glyph(
  <>
    <path d="M4 7h16M4 12h16M4 17h10" />
    <circle cx="19" cy="17" r="2" />
  </>
);

export const IconChart = glyph(
  <>
    <path d="M4 19h16" />
    <path d="M6 15l4-4 3 3 5-6" />
    <circle cx="6" cy="15" r="1.2" />
    <circle cx="18" cy="8" r="1.2" />
  </>
);

export const IconUsers = glyph(
  <>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 19a6 6 0 0 1 12 0" />
    <path d="M16 5.3a3.2 3.2 0 0 1 0 5.4M17.5 19a6 6 0 0 0-2.2-4.6" />
  </>
);

export const IconShield = glyph(
  <>
    <path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 9.5-4.1-1.9-7-5.3-7-9.5V6z" />
    <path d="M9.2 12.2l2 2 3.6-3.8" />
  </>
);

export const IconBell = glyph(
  <>
    <path d="M18 9a6 6 0 1 0-12 0c0 4-1.6 5.3-1.6 5.3h15.2S18 13 18 9z" />
    <path d="M10.3 18a2 2 0 0 0 3.4 0" />
  </>
);

export const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12.5l5.2 5.2L20 7" />
  </svg>
);

export const IconArrow = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h13M12.5 5.5L19 12l-6.5 6.5" />
  </svg>
);

export const IconChevron = ({ dir = "right" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === "left" ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
  </svg>
);
